import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  CreditCard,
  CheckCircle2,
  XCircle,
  Loader2,
  ArrowLeft,
  Shield,
  TestTube,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TestPayment() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: ''
  });

  // Check if this is a return from payment
  const isSuccess = window.location.pathname.includes('/success');
  const isCancelled = window.location.pathname.includes('/cancelled');
  const testRef = searchParams.get('ref');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.name || !form.email) {
      toast.error('Please enter your name and email');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/subscriptions/test-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to initiate payment');
      }

      const data = await res.json();
      
      // Create and submit form to PayFast
      const payForm = document.createElement('form');
      payForm.method = 'POST';
      payForm.action = data.gateway_url;
      
      Object.entries(data.payment_params).forEach(([key, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = value;
        payForm.appendChild(input);
      });
      
      document.body.appendChild(payForm);
      payForm.submit();
      
    } catch (error) {
      toast.error(error.message);
      setLoading(false);
    }
  };

  // Success page
  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-6 text-center">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Payment Successful!</h1>
            <p className="text-slate-600 mb-6">
              Your R10 test payment was processed successfully. PayFast integration is working correctly.
            </p>
            {testRef && (
              <p className="text-sm text-slate-500 mb-6">
                Reference: <span className="font-mono">{testRef}</span>
              </p>
            )}
            <div className="space-y-3">
              <Link to="/dashboard">
                <Button className="w-full">Go to Dashboard</Button>
              </Link>
              <Link to="/">
                <Button variant="outline" className="w-full">Back to Home</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Cancelled page
  if (isCancelled) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-6 text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Payment Cancelled</h1>
            <p className="text-slate-600 mb-6">
              Your test payment was cancelled. No charges were made.
            </p>
            <div className="space-y-3">
              <Link to="/test-payment">
                <Button className="w-full">Try Again</Button>
              </Link>
              <Link to="/">
                <Button variant="outline" className="w-full">Back to Home</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main test payment form
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-100">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="BodyCorp365" className="h-8" onError={(e) => e.target.style.display = 'none'} />
            <span className="text-xl font-bold text-slate-900">BodyCorp365</span>
          </Link>
          <Link to="/">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-lg mx-auto px-4 py-12">
        <Card>
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <TestTube className="w-8 h-8 text-blue-600" />
            </div>
            <CardTitle className="text-2xl">Test Payment</CardTitle>
            <CardDescription>
              Make a R10 test payment to verify PayFast integration
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="John Smith"
                  className="mt-1"
                  required
                  data-testid="test-payment-name"
                />
              </div>

              <div>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="john@example.com"
                  className="mt-1"
                  required
                  data-testid="test-payment-email"
                />
              </div>

              <div>
                <Label htmlFor="phone">Phone (Optional)</Label>
                <Input
                  id="phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+27 82 123 4567"
                  className="mt-1"
                />
              </div>

              {/* Payment Summary */}
              <div className="bg-slate-50 rounded-lg p-4 mt-6">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-slate-600">Test Payment</span>
                  <span className="font-semibold">R10.00</span>
                </div>
                <div className="flex justify-between items-center text-lg font-bold border-t pt-2">
                  <span>Total</span>
                  <span className="text-emerald-600">R10.00</span>
                </div>
              </div>

              {/* PayFast Badge */}
              <div className="flex items-center justify-center gap-2 text-sm text-slate-500 mt-4">
                <Shield className="w-4 h-4" />
                <span>Secured by PayFast</span>
              </div>

              <Button 
                type="submit" 
                className="w-full mt-6" 
                size="lg"
                disabled={loading}
                data-testid="test-payment-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Redirecting to PayFast...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-4 h-4 mr-2" />
                    Pay R10.00
                  </>
                )}
              </Button>

              <p className="text-xs text-slate-500 text-center mt-4">
                This is a real payment of R10. Use this to test the PayFast integration 
                before going live with actual subscriptions.
              </p>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
